import React, { useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { ISSUE_CATEGORIES, reportIssue } from "../services/boxDetailApi";

import Modal from "./Modal";
import { Button } from "./ui/button";

/**
 * "Report Maintenance Need" — opens a manual maintenance ticket for a box.
 * Available to any role that can see the box (customers/vendors/platform). The
 * ticket creator is set from the login server-side; there is no name field.
 */
export const ReportMaintenanceButton: React.FC<{
  scu: string;
  /** Display label for the modal title (smartbox code/name). */
  label?: string | null;
  /** "link" (compact, for table rows) or "button". */
  variant?: "link" | "button";
}> = ({ scu, label, variant = "link" }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"low" | "normal" | "high">("normal");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneId, setDoneId] = useState<number | null>(null);

  const reset = (): void => {
    setCategory("");
    setDescription("");
    setPriority("normal");
    setError(null);
    setDoneId(null);
  };
  const close = (): void => {
    setOpen(false);
    reset();
  };

  const submit = async (): Promise<void> => {
    if (!token) return;
    if (!category) {
      setError("Pick an issue type.");
      return;
    }
    if (category === "other" && !description.trim()) {
      setError("Please describe the issue.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await reportIssue(
        scu,
        { category, description: description.trim() || undefined, priority },
        token,
      );
      setDoneId(r.ticket_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const openModal = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  return (
    <>
      {variant === "button" ? (
        <Button type="button" size="sm" variant="outline" onClick={openModal}>
          Report maintenance need
        </Button>
      ) : (
        <button
          type="button"
          // Leaflet's popup CSS underlines <a> tags but not <button>. The
          // "Report issue" trigger sits next to two Link elements in the
          // map popup and should read as the same style -- so add an
          // explicit underline. `hover:no-underline` would break the
          // consistency; keep the underline persistent to match the
          // adjacent links' rendered state.
          onClick={openModal}
          className="text-xs text-primary-600 underline hover:no-underline"
        >
          Report issue
        </button>
      )}

      <Modal
        open={open}
        onClose={close}
        title={`Report maintenance · ${label ?? scu}`}
        widthClass="max-w-md"
      >
        {doneId !== null ? (
          <div className="space-y-3">
            <div className="text-sm text-success-700 bg-success-50 px-3 py-2 rounded">
              ✓ Ticket #{doneId} opened. Maintenance has been notified.
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={reset}>
                Report another
              </Button>
              <Button type="button" size="sm" onClick={close}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {error && (
              <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
            )}
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Issue type
              </span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm bg-white"
                autoFocus
              >
                <option value="">Select…</option>
                {ISSUE_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Details {category === "other" ? "(required)" : "(optional)"}
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What's wrong? Anything maintenance should know."
                className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <div className="flex items-end justify-between gap-3">
              <label className="block">
                <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                  Priority
                </span>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as "low" | "normal" | "high")}
                  className="border border-secondary-300 rounded px-2 py-1.5 text-sm bg-white"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </label>
              <Button type="button" size="sm" disabled={busy} onClick={() => void submit()}>
                {busy ? "Submitting…" : "Open ticket"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ReportMaintenanceButton;
